// FLAGS: identity, personal-data, silent-failure, legacy
// The browser's session state and the only writer of it: `isLoggedIn` here is what `protectedRoute` in
// routes.tsx reads, its initial value is rebuilt from sessionStorage on every page load, and several cases
// reach out of the reducer to write that storage and to send the user's identity to PostHog.
import {
  REGISTER_SUCCESS,
  REGISTER_FAIL,
  LOGIN_SUCCESS,
  LOGIN_FAIL,
  TWO_FACTOR_PENDING,
  TWO_FACTOR_SUCCESS,
  LOGOUT,
  RESEND_CODE_SUCCESS,
  RESEND_CODE_FAIL,
  VERIFY_FAIL,
  VERIFY_SUCCESS,
  FORGOT_PASSWORD_SUCCESS,
  FORGOT_PASSWORD_FAIL,
  CLEAR_MESSAGE,
  EMAIL_VERIFY_SUCCESS,
  EMAIL_VERIFY_FAIL,
  REGISTER_GUEST_SUCCESS,
  UPDATE_SUCCESS,
  UPDATE_FAIL,
  REGISTER_EXTERNAL_SUCCESS,
  PROFILE_SUCCESS,
  PROFILE_FAIL,
} from "@src/actions/typeActions";
import authService from "@src/services/auth.service";
import { posthogService } from "@src/services/posthog.service";
import { mapUserToAnalyticsProps } from "@src/utils/analyticsUtils";

// FLAGS: identity
// Read once at module load, so a stored user is enough to start the app as logged in before any request is
// made — the profile fetch in App.tsx is what later confirms or contradicts it.
const user = authService.getUserFromStorage();

const initialState = user
  ? {
      isLoggedIn: true,
      twoFactorPending: false,
      user,
      error: null,
      successMessage: null,
      warningMessage: null,
    }
  : {
      isLoggedIn: false,
      twoFactorPending: false,
      user: null,
      error: null,
      successMessage: null,
      warningMessage: null,
    };

// eslint-disable-next-line default-param-last, @typescript-eslint/default-param-last
const authReducers = (state = initialState, action) => {
  const { type, payload } = action;
  switch (type) {
    case REGISTER_EXTERNAL_SUCCESS:
      posthogService.identifyUser(mapUserToAnalyticsProps(payload.user));
      return {
        ...state,
        isLoggedIn: true,
        user: payload.user,
        error: null,
        successMessage: payload.successMessage,
        warningMessage: payload.warningMessage,
      };
    case REGISTER_SUCCESS:
      posthogService.identifyUser(mapUserToAnalyticsProps(payload.user));
      return {
        ...state,
        isLoggedIn: false,
        user: payload.user,
        error: null,
        successMessage: payload.successMessage,
        warningMessage: payload.warningMessage,
      };
    case UPDATE_SUCCESS:
      return {
        ...state,
        user: { ...payload.user },
        error: null,
        successMessage: payload.successMessage,
        warningMessage: payload.warningMessage,
      };
    case REGISTER_GUEST_SUCCESS:
      return {
        ...state,
        isLoggedIn: false,
        user: payload.user,
        error: null,
        successMessage: payload.successMessage,
        warningMessage: payload.warningMessage,
      };
    case REGISTER_FAIL:
      return {
        ...state,
        isLoggedIn: false,
        user: payload.user,
        error: payload.error,
      };
    // FLAGS: identity
    // `saveUserToStorage(null)` is a *clear*, and userActions.update dispatches this case with `user: null` —
    // so a rejected profile save empties the stored user and drops `isLoggedIn`, signing the tab out.
    case UPDATE_FAIL:
      authService.saveUserToStorage(payload.user);
      return {
        ...state,
        isLoggedIn: false,
        user: payload.user,
        error: payload.error,
      };
    case LOGIN_SUCCESS:
      authService.saveUserToStorage(payload.user);
      posthogService.identifyUser(mapUserToAnalyticsProps(payload.user));
      return {
        ...state,
        isLoggedIn: true,
        user: payload.user,
        error: null,
        successMessage: payload.successMessage,
        warningMessage: payload.warningMessage,
      };
    case LOGIN_FAIL:
      return {
        ...state,
        isLoggedIn: false,
        user: payload?.user,
        error: payload?.error,
      };
    case TWO_FACTOR_PENDING:
      authService.saveUserToStorage(payload?.user);
      return {
        ...state,
        isLoggedIn: false,
        twoFactorPending: true,
        user: payload?.user,
      };
    case TWO_FACTOR_SUCCESS:
      authService.saveUserToStorage(payload?.user);
      posthogService.identifyUser(mapUserToAnalyticsProps(payload.user));
      return {
        ...state,
        isLoggedIn: true,
        twoFactorPending: false,
        user: payload?.user,
        error: null,
        successMessage: payload.successMessage,
        warningMessage: payload.warningMessage,
      };
    case LOGOUT:
      authService.logout();
      return {
        ...state,
        isLoggedIn: false,
        user: null,
        error: null,
        twoFactorPending: false,
        successMessage: null,
        warningMessage: null,
      };
    case RESEND_CODE_SUCCESS:
      return {
        ...state,
        isLoggedIn: false,
        error: null,
        successMessage: payload.successMessage,
        warningMessage: payload.warningMessage,
      };
    case RESEND_CODE_FAIL:
      return {
        ...state,
        isLoggedIn: false,
        error: payload.error,
      };
    case FORGOT_PASSWORD_SUCCESS:
      return {
        ...state,
        isLoggedIn: false,
        user: payload.user,
        error: null,
        successMessage: payload.successMessage,
        warningMessage: payload.warningMessage,
      };
    case FORGOT_PASSWORD_FAIL:
      return {
        ...state,
        isLoggedIn: false,
        error: payload.error,
      };
    case VERIFY_SUCCESS:
      posthogService.identifyUser(mapUserToAnalyticsProps(payload.user));
      return {
        ...state,
        isLoggedIn: true,
        user: payload.user,
        error: null,
        successMessage: payload.successMessage,
        warningMessage: payload.warningMessage,
      };
    case VERIFY_FAIL:
      return {
        ...state,
        isLoggedIn: false,
        user: payload.user,
        error: payload.error,
      };
    case EMAIL_VERIFY_SUCCESS:
      posthogService.identifyUser(mapUserToAnalyticsProps(payload.user));
      return {
        ...state,
        isLoggedIn: false,
        user: null,
        error: null,
        successMessage: payload.successMessage,
        warningMessage: payload.warningMessage,
      };
    case EMAIL_VERIFY_FAIL:
      return {
        ...state,
        isLoggedIn: false,
        user: payload.user,
        error: payload.error,
        successMessage: payload.successMessage,
        warningMessage: payload.warningMessage,
      };
    case CLEAR_MESSAGE:
      return {
        ...state,
        error: null,
        successMessage: null,
        warningMessage: null,
      };
    case PROFILE_SUCCESS:
      authService.saveUserToStorage(payload.user);
      return {
        ...state,
        isLoggedIn: true,
        user: payload.user,
        error: null,
      };
    // FLAGS: identity
    // Unlike LOGOUT this does not call `authService.logout()`, so the cookie and the stored user both
    // survive: the app renders as signed out now and signs itself back in from storage on the next load.
    case PROFILE_FAIL:
      return {
        ...state,
        isLoggedIn: false,
        user: null,
        error: null,
      };
    default:
      return state;
  }
};

export const authReducerSelector = (state) => state.authReducers;
export const userSelector = (state) => authReducerSelector(state).user;
export default authReducers;
